/**
 * Meta Pixel Sprint 2026-08 — cookie consent banner + settings modal.
 *
 * The banner shows exactly when ``readConsent().decided === false``.
 * Three primary buttons: "Alle akzeptieren" (marketing:true), "Alle
 * ablehnen" (marketing:false), "Einstellungen" (opens the per-
 * category modal).
 *
 * We keep the visual footprint SMALL — one bottom-anchored sheet on
 * desktop, a full-width sheet on mobile. No emojis, no dark patterns,
 * equal weight for accept and reject.
 */
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  readConsent,
  acceptAll,
  rejectAll,
  savePartial,
  subscribe as subscribeConsent,
} from "./consent";

function _cx(...c) {
  return c.filter(Boolean).join(" ");
}

/** Standalone hook — always safe to render, decides internally
 *  whether the banner is shown. Consumers just mount ``<ConsentBanner />``
 *  once at the app root. */
export default function ConsentBanner() {
  const { t } = useTranslation("consent");
  const [state, setState] = useState(() => readConsent());
  const [settingsOpen, setSettingsOpen] = useState(false);
  const sheetRef = useRef(null);
  const bannerVisible = !state.decided && !settingsOpen;

  useEffect(() => subscribeConsent(setState), []);

  // Sticky bottom bars (mobile search CTA, tab bar) read `--consent-h`
  // so the banner never covers them (iteration_109 HIGH bug).
  useEffect(() => {
    const root = document.documentElement;
    if (!bannerVisible) {
      root.style.setProperty("--consent-h", "0px");
      return undefined;
    }
    const apply = () => {
      const h = sheetRef.current?.getBoundingClientRect().height || 0;
      root.style.setProperty("--consent-h", `${Math.round(h)}px`);
    };
    apply();
    const ro = new ResizeObserver(apply);
    if (sheetRef.current) ro.observe(sheetRef.current);
    window.addEventListener("resize", apply);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", apply);
      root.style.setProperty("--consent-h", "0px");
    };
  }, [bannerVisible]);

  if (state.decided && !settingsOpen) return null;

  const closeAndAcceptAll = () => acceptAll();
  const closeAndRejectAll = () => rejectAll();

  if (!state.decided && !settingsOpen) {
    return (
      <div
        role="dialog"
        aria-label={t("banner.title")}
        aria-modal="false"
        data-testid="consent-banner"
        ref={sheetRef}
        className="fixed inset-x-0 bottom-0 z-[9999] p-3 sm:p-4"
      >
        <div className="mx-auto flex max-w-2xl flex-col gap-3 rounded-2xl border border-zinc-800 bg-zinc-950/95 px-4 py-3 shadow-2xl shadow-black/50 backdrop-blur-md sm:flex-row sm:items-center sm:gap-4">
          <p className="flex-1 text-xs leading-relaxed text-zinc-300">
            {t("banner.body_short")}{" "}
            <button
              type="button"
              data-testid="consent-settings-open"
              onClick={() => setSettingsOpen(true)}
              className="whitespace-nowrap text-emerald-400 underline underline-offset-2 hover:text-emerald-300"
            >
              {t("banner.settings")}
            </button>
          </p>
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              data-testid="consent-reject-all"
              onClick={closeAndRejectAll}
              className="rounded-full border border-zinc-700 px-4 py-1.5 text-xs font-medium text-zinc-200 transition-colors hover:bg-zinc-900"
            >
              {t("banner.reject_all")}
            </button>
            <button
              type="button"
              data-testid="consent-accept-all"
              onClick={closeAndAcceptAll}
              className="rounded-full bg-emerald-500 px-5 py-1.5 text-xs font-semibold text-zinc-950 transition-colors hover:bg-emerald-400"
            >
              {t("banner.accept_all")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ConsentSettingsModal
      initial={state.categories}
      onCancel={() => setSettingsOpen(false)}
      onSave={(cats) => {
        savePartial(cats);
        setSettingsOpen(false);
      }}
    />
  );
}

function ConsentSettingsModal({ initial, onCancel, onSave }) {
  const { t } = useTranslation("consent");
  const [marketing, setMarketing] = useState(Boolean(initial?.marketing));
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("settings.title")}
      data-testid="consent-settings-modal"
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 p-4"
    >
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg border border-zinc-800 bg-zinc-950 p-6 text-zinc-100 shadow-2xl">
        <h2 className="mb-2 text-lg font-semibold">{t("settings.title")}</h2>
        <p className="mb-4 text-sm text-zinc-400">{t("settings.description")}</p>

        <div className="mb-3 rounded border border-zinc-800 bg-zinc-900/60 p-3">
          <div className="flex items-center justify-between">
            <div className="font-medium">
              {t("settings.categories.necessary.title")}
            </div>
            <span className="rounded bg-zinc-800 px-2 py-1 text-xs uppercase tracking-wider text-zinc-300">
              {t("settings.categories.necessary.always_on")}
            </span>
          </div>
          <div className="mt-1 text-xs text-zinc-400">
            {t("settings.categories.necessary.description")}
          </div>
        </div>

        <label
          className="mb-4 flex cursor-pointer items-start gap-3 rounded border border-zinc-800 bg-zinc-900/60 p-3"
          htmlFor="consent-toggle-marketing"
        >
          <input
            id="consent-toggle-marketing"
            data-testid="consent-toggle-marketing"
            type="checkbox"
            checked={marketing}
            onChange={(e) => setMarketing(e.target.checked)}
            className="mt-1 h-4 w-4 accent-white"
          />
          <span>
            <span className="block font-medium">
              {t("settings.categories.marketing.title")}
            </span>
            <span className="mt-1 block text-xs text-zinc-400">
              {t("settings.categories.marketing.description")}
            </span>
            <span
              className="mt-3 block text-xs font-medium text-zinc-300"
              data-testid="consent-marketing-details-heading"
            >
              {t("settings.categories.marketing.details_heading")}
            </span>
            <ul
              className="mt-1 list-disc space-y-1 pl-4 text-xs text-zinc-400"
              data-testid="consent-marketing-details"
            >
              {(
                t("settings.categories.marketing.details", {
                  returnObjects: true,
                }) || []
              ).map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ul>
          </span>
        </label>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            data-testid="consent-settings-cancel"
            onClick={onCancel}
            className="rounded border border-zinc-700 px-4 py-2 text-sm hover:bg-zinc-900"
          >
            {t("settings.cancel")}
          </button>
          <button
            type="button"
            data-testid="consent-settings-save"
            onClick={() => onSave({ marketing })}
            className="rounded bg-white px-4 py-2 text-sm font-medium text-zinc-950 hover:bg-zinc-200"
          >
            {t("settings.save")}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Named export — a tiny link used by page footers to re-open the
 *  settings modal any time (revocation entry point). */
export function ConsentFooterLink({ className }) {
  const { t } = useTranslation("consent");
  return (
    <button
      type="button"
      data-testid="consent-footer-link"
      onClick={() => {
        // Force banner + modal open by clearing the decision.
        // Using ``clearConsent`` from the store would emit an event
        // and any active tracker would immediately disable. The
        // banner then renders with settings open.
        import("./consent").then((m) => m.clearConsent());
      }}
      className={_cx(
        "text-xs uppercase tracking-[0.18em] text-zinc-500 hover:text-zinc-200",
        className,
      )}
    >
      {t("footer_link")}
    </button>
  );
}
